import { useState } from "react";

import { ConfirmDialog } from "primereact/confirmdialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { ProgressSpinner } from "primereact/progressspinner";

import { themeClasses, C } from "sj-ui-toolkit/src/atlas/utils";
import { useUserContext } from "sj-ui-toolkit/src/atlas/contexts/User/UserContextProvider";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import { useAspenGlobalEnabled } from "sj-ui-toolkit/src/hooks";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import {
  useAspenChat,
  ChatInput,
  ChatSessionSidebar,
} from "sj-ui-toolkit/src/atlas/components/AspenChat";

import { AspenWelcome } from "./AspenWelcome";
import { ChatMessageList } from "./ChatMessageList";
import { useExampleAssignment } from "./useExampleAssignment";
import S from "./AspenChatBeta.module.scss";

// Beta scope is assignments only; the example pre-fills the input and selects the assignment name.
const EXAMPLE_PROMPT_PREFIX = "Show me the status of everyone in the ";
const EXAMPLE_PROMPT_SUFFIX = " assignment";
const DISCLAIMER_ID = "aspen-chat-disclaimer";
const EXAMPLE_HINT_ID = "aspen-chat-example-hint";

export default function AspenChatBeta() {
  const {
    selectedTenant,
    sessionId,
    sessions,
    sessionsLoading,
    sessionsError,
    sessionsHasMore,
    transcriptLoading,
    messages,
    input,
    setInput,
    streaming,
    error,
    pendingSelection,
    setPendingSelection,
    hasMessages,
    sendMessage,
    stopStreaming,
    selectSession,
    handleNewChat,
  } = useAspenChat();
  const { user } = useUserContext();
  const { aspenGloballyEnabled, isLoading: aspenSettingLoading } =
    useAspenGlobalEnabled(selectedTenant);
  // Below lg, ChatSessionSidebar becomes an off-canvas drawer toggled by this state.
  const [historyOpen, setHistoryOpen] = useState(false);
  const [heroDismissed, setHeroDismissed] = useState(false);

  const aspenReady = !aspenSettingLoading;
  const aspenDisabled = aspenReady && !aspenGloballyEnabled;
  const chatInputDisabled = !aspenReady || aspenDisabled;
  const welcomeReady = aspenReady && !transcriptLoading;
  const assignmentName = useExampleAssignment(aspenReady && !aspenDisabled ? selectedTenant : "");

  const givenName = user?.name?.givenName;
  const greetingName = givenName && givenName !== "Anonymous" ? givenName : undefined;

  const exampleAssignmentName = assignmentName ?? "Assignment name";
  const examplePrompt = `${EXAMPLE_PROMPT_PREFIX}${exampleAssignmentName}${EXAMPLE_PROMPT_SUFFIX}`;
  const exampleApplied = !hasMessages && input === examplePrompt;

  const heroVisible = welcomeReady && !hasMessages && !aspenDisabled && !heroDismissed;

  const handleTryExample = () => {
    setHeroDismissed(true);
    setInput(examplePrompt);
    setPendingSelection([
      EXAMPLE_PROMPT_PREFIX.length,
      EXAMPLE_PROMPT_PREFIX.length + exampleAssignmentName.length,
    ]);
  };

  const startNewChat = () => {
    handleNewChat();
    setHeroDismissed(false);
    setHistoryOpen(false);
  };

  return (
    <>
      <ConfirmDialog />

      <div className={C(S.chatRoot, "flex")}>
        {!heroVisible && <h1 className="sr-only">Aspen chat</h1>}

        <ChatSessionSidebar
          sessions={sessions}
          selectedSessionId={sessionId}
          loading={sessionsLoading}
          error={sessionsError}
          hasMore={sessionsHasMore}
          streaming={streaming}
          onNewChat={startNewChat}
          onSelectSession={sid => {
            selectSession(sid);
            setHistoryOpen(false);
          }}
          mobileOpen={historyOpen}
          onMobileClose={() => setHistoryOpen(false)}
        />

        <div className="flex flex-col flex-1 min-w-0">
          <div
            className={C(
              themeClasses.bgElevation1,
              "lg:hidden w-full p-3 flex justify-end gap-2 border-b border-surface-300"
            )}
          >
            <Button
              icon="pi pi-history"
              severity="secondary"
              size="small"
              onClick={() => setHistoryOpen(true)}
              aria-label="Show chat history"
            />
            <Button
              label="New chat"
              icon="pi pi-plus"
              size="small"
              onClick={startNewChat}
              disabled={streaming}
              aria-label="Start new chat"
            />
          </div>

          {aspenDisabled && (
            <div className={S.column}>
              <AspenDisabledNotice />
            </div>
          )}

          {hasMessages ? (
            <ChatMessageList
              messages={messages}
              transcriptLoading={transcriptLoading}
              error={error}
              streaming={streaming}
            />
          ) : welcomeReady ? (
            <div className={C(S.emptyMessageContainer, themeClasses.scrollbar)}>
              <AspenWelcome
                layout={heroVisible ? "hero" : "message"}
                greetingName={greetingName}
                disabled={aspenDisabled}
                assignmentName={assignmentName}
                onTryExample={handleTryExample}
              />
            </div>
          ) : (
            <div className={S.loading} role="status" aria-live="polite" aria-busy="true">
              <ProgressSpinner style={{ width: "18px", height: "18px" }} strokeWidth="5" />
              {transcriptLoading ? "Loading chat session..." : "Loading Aspen..."}
            </div>
          )}

          <div className="sticky bottom-0 py-3">
            <div className="w-full max-w-4xl mx-auto px-4">
              <ChatInput
                input={input}
                streaming={streaming}
                disabled={chatInputDisabled}
                placeholder={
                  aspenDisabled ? "Enable Aspen to start a chat" : "Ask about an assignment…"
                }
                ariaDescribedBy={
                  exampleApplied ? `${DISCLAIMER_ID} ${EXAMPLE_HINT_ID}` : DISCLAIMER_ID
                }
                onInput={setInput}
                onSend={sendMessage}
                onStop={stopStreaming}
                pendingSelection={pendingSelection}
                onSelectionApplied={() => setPendingSelection(null)}
              />
              {exampleApplied && (
                <p id={EXAMPLE_HINT_ID} className="text-xs text-muted-aaa mt-2">
                  Assignment name is selected — start typing to swap it for another.
                </p>
              )}
              <p id={DISCLAIMER_ID} className="text-xs text-muted-aaa text-center mt-2">
                Aspen can make mistakes. Verify important information before acting on it.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function AspenDisabledNotice() {
  const { navigateTo } = useSmartNavigation();
  const settingsRoute = AdminAppRoutes.routes.aspen.routes.settings.route;

  const handleSettingsNavigation = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    navigateTo(settingsRoute);
  };

  return (
    <Card
      className={S.aspenDisabledNotice}
      pt={{ body: { className: "p-3" }, content: { className: "p-0" } }}
      data-testid="aspen-chat-disabled-notice"
    >
      <div className="flex items-start gap-2">
        <i aria-hidden="true" className={C(themeClasses.textWarning, "pi pi-info-circle mt-0.5")} />
        <p className="text-sm">
          Aspen is currently disabled in your tenant.{" "}
          <a
            href={settingsRoute}
            className="text-primary-darker underline font-semibold"
            onClick={handleSettingsNavigation}
          >
            Enable it here
          </a>{" "}
          to start using it with your assignments and other platform insights.
        </p>
      </div>
    </Card>
  );
}
